import { useEffect, useState } from "react";
import { useAuth } from "../auth.jsx";
import { Button, Card, PageHeader, Spinner } from "../components/ui.jsx";
import { api } from "../lib/api.js";

export default function ComposeServices() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [selectedUuid, setSelectedUuid] = useState("");
  const [selected, setSelected] = useState(null);
  const [composeYaml, setComposeYaml] = useState("");
  const [savedYaml, setSavedYaml] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const dirty = composeYaml !== savedYaml;

  async function loadService(uuid, { skipDirtyCheck = false } = {}) {
    if (!skipDirtyCheck && dirty && !window.confirm("Discard your unsaved Compose edits?")) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const service = await api.composeService(uuid);
      setSelectedUuid(uuid);
      setSelected(service);
      setComposeYaml(service.composeYaml || "");
      setSavedYaml(service.composeYaml || "");
    } catch (e) {
      setError(e.message || "Could not load this Compose service.");
    } finally {
      setBusy(false);
    }
  }

  async function refresh() {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await api.composeServices();
      const list = result.services || [];
      setServices(list);
      if (selectedUuid && list.some((service) => service.uuid === selectedUuid)) {
        await loadService(selectedUuid, { skipDirtyCheck: true });
      } else if (!selectedUuid && list[0]) {
        await loadService(list[0].uuid, { skipDirtyCheck: true });
      } else if (!list.length) {
        setSelected(null);
        setSelectedUuid("");
        setComposeYaml("");
        setSavedYaml("");
      }
    } catch (e) {
      setError(e.message || "Could not list Compose services.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function save() {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await api.saveComposeService(selectedUuid, composeYaml);
      setSavedYaml(composeYaml);
      setSelected((current) => current ? { ...current, name: result.name || current.name } : current);
      setNotice("Compose definition saved. It has not been deployed.");
    } catch (e) {
      setError(e.message || "Could not save the Compose definition.");
    } finally {
      setBusy(false);
    }
  }

  async function deploy() {
    if (dirty) {
      setError("Save the Compose definition before deploying it.");
      return;
    }
    if (!window.confirm(`Queue a deployment of ${selected?.name || "this Compose stack"}? This can change live services.`)) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await api.deployComposeService(selectedUuid);
      setNotice(result.message || "Compose deployment queued.");
    } catch (e) {
      setError(e.message || "Could not queue the Compose deployment.");
    } finally {
      setBusy(false);
    }
  }

  if (user?.role !== "admin") {
    return <div className="page"><PageHeader title="Compose stacks" /><Card>Only administrators can manage private Compose stacks.</Card></div>;
  }

  return (
    <div className="page space-y-5">
      <PageHeader
        title="Compose stacks"
        subtitle="Review and edit operator-managed Coolify Compose definitions. Saving a definition does not deploy it."
        actions={<Button disabled={busy || dirty} onClick={refresh}>{busy ? <Spinner /> : "Refresh"}</Button>}
      />

      {error && <div role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && <div role="status" className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800">{notice}</div>}

      <div className="grid gap-5 xl:grid-cols-[280px_minmax(0,1fr)]">
        <Card>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold" style={{ color: "var(--text)" }}>Private services</h2>
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>{services.length}</span>
          </div>
          {services.length === 0 && !busy && <p className="text-sm" style={{ color: "var(--text-muted)" }}>No Compose services found in Coolify.</p>}
          <div className="grid gap-1">
            {services.map((service) => (
              <button
                key={service.uuid}
                type="button"
                disabled={busy}
                onClick={() => loadService(service.uuid)}
                className="rounded-md px-3 py-2 text-left text-sm disabled:opacity-60"
                style={{
                  background: selectedUuid === service.uuid ? "var(--accent-soft)" : "transparent",
                  color: selectedUuid === service.uuid ? "var(--accent-text)" : "var(--text)",
                  border: "1px solid var(--border)",
                }}
              >
                <span className="block font-medium">{service.name || "Unnamed service"}</span>
                <span className="mt-1 block truncate font-mono text-[10px]" style={{ color: "var(--text-muted)" }}>{service.uuid}</span>
              </button>
            ))}
          </div>
        </Card>

        <Card>
          {!selected && <div className="py-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>{busy ? <><Spinner /> Loading Compose services…</> : "Select a Compose service to view its definition."}</div>}
          {selected && <>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold" style={{ color: "var(--text)" }}>{selected.name}</h2>
                <p className="mt-1 font-mono text-xs" style={{ color: "var(--text-muted)" }}>{selected.uuid}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {dirty && <span className="text-xs" style={{ color: "var(--warn)" }}>Unsaved changes</span>}
                <Button disabled={busy || !dirty || !composeYaml.trim()} onClick={save}>{busy ? <Spinner /> : "Save definition"}</Button>
                <Button variant="primary" disabled={busy || dirty} onClick={deploy}>{busy ? <Spinner /> : "Deploy"}</Button>
              </div>
            </div>
            <label htmlFor="compose-definition" className="mb-2 block text-xs font-medium" style={{ color: "var(--text-muted)" }}>Docker Compose YAML</label>
            <textarea
              id="compose-definition"
              value={composeYaml}
              onChange={(event) => { setComposeYaml(event.target.value); setError(""); setNotice(""); }}
              spellCheck={false}
              aria-label="Docker Compose definition"
              className="min-h-[60vh] w-full resize-y rounded-lg border px-3 py-3 font-mono text-xs leading-5 outline-none focus:ring-2"
              style={{ background: "var(--bg)", color: "var(--text)", borderColor: "var(--border)" }}
            />
            <p className="mt-2 text-xs" style={{ color: "var(--text-muted)" }}>Maximum 64 KiB. Save updates the definition in Coolify; Deploy is a separate action.</p>
          </>}
        </Card>
      </div>
    </div>
  );
}
